import { useState, useEffect } from "react";

const useCustomFormValidation = (initialValues, validationRules) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Determine if the new value needs to be wrapped in an object
    const isCurrentValueObject = values ? typeof values[name] === "object" : "";
    const newValue = isCurrentValueObject ? { value } : value;
    setValues({ ...values, [name]: newValue });
    setTouched({ ...touched, [name]: true });
    validateField(name, newValue);
  };

  const handleBlur = (e) => {
    if (e) {
      const { name, value } = e?.target;

      const isCurrentValueObject = typeof values[name] === "object";
      const newValue = isCurrentValueObject ? { value } : value;

      setTouched({ ...touched, [name]: true });
      validateField(name, newValue);
    }
  };

  const validateField = (name, value) => {
    const rule = validationRules[name];

    const actualValue = typeof value === "object" && value !== null ? value.value : value;

    if (rule) {
      let fieldErrors = [];
      if (Array.isArray(rule)) {
        fieldErrors = rule.map((ruleItem) => validateRule(ruleItem, actualValue));
      } else {
        fieldErrors.push(validateRule(rule, actualValue));
      }
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: fieldErrors.find((error) => error !== undefined) || undefined,
      }));
    }
  };

  const validateRule = (rule, value) => {
    value = value !== null ? String(value) : null;
    const actualValue = typeof value === "object" && value !== null ? value.value : value;

    if (rule.required && (actualValue?.trim() === "" || actualValue?.trim() === null)) {
      return rule.errorMessage || "This field is required.";
    }

    if (rule.email && actualValue && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(actualValue)) {
      return rule.errorMessage || "Invalid email format.";
    }
    if (rule.number && !/^\d+$/.test(actualValue)) {
      return rule.errorMessage || "Must be a number.";
    }
    if (rule.domain) {
      const domainRegex =
        /\.(com|net|org|edu|gov|biz|info|co|io|us|uk|in|au|ca|de|fr|jp|kr|ru|cn|br|mx|es|nl|ch|se|no|fi|dk|it|be|at)$/i;
      if (actualValue && !domainRegex?.test(actualValue)) {
        return rule.errorMessage || "Provide valid website URL.";
      }
    }

    if (rule.link) {
      const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:[0-9]{1,5})?(\/\S*)?$/;
      if (actualValue && !urlRegex?.test(actualValue.trim())) {
        return "Must be a valid link.";
      }
    }
    if (rule.maxDigits && actualValue?.length > rule?.maxDigits) {
      return rule.errorMessage || `Must be between 1 to ${rule?.maxDigits} digits.`;
    }
    if (rule.minDigits && actualValue?.length < rule?.minDigits) {
      return rule.errorMessage || `Must be at least ${rule?.minDigits} digits.`;
    }

    if (rule.validationFn && !rule.validationFn(actualValue)) {
      return rule.errorMessage || "Invalid value.";
    }
    if (rule.minLength && actualValue.length < rule.minLength) {
      return rule.errorMessage || `Must be at least ${rule?.minLength} characters.`;
    }

    return undefined;
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(validationRules).forEach((name) => {
      const rule = validationRules[name];
      const value = values[name];

      let fieldErrors = [];

      const actualValue = typeof value === "object" && value !== null ? value?.value : value;
      if (name === "sub_tasks") {
        if (value.length !== 0) {
          value.map((item, index) => {
            if (fieldErrors.length === 0) {
              fieldErrors.push({ subject: [], assigned_to: [] });
            }

            if (item.subject === "") {
              fieldErrors[0].subject.push({ value: index });
            } else {
              fieldErrors[0].subject.push({ value: "subjectFound" });
            }

            if (item.assigned_to.length === 0) {
              fieldErrors[0].assigned_to.push({ value: index });
            } else {
              fieldErrors[0].assigned_to.push({ value: "assignedToFound" });
            }
          });
        }
      } else if (Array.isArray(rule)) {
        fieldErrors = rule.map((ruleItem) => validateRule(ruleItem, actualValue));
      } else {
        fieldErrors.push(validateRule(rule, actualValue));
      }

      newErrors[name] = fieldErrors.find((error) => error !== undefined) || undefined;
    });

    setErrors(newErrors);
    return newErrors;
  };

  const isFormValid = () => {
    const formValidation = validateForm();
    return Object.keys(formValidation).every((key) => formValidation[key] === undefined);
  };

  return {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateField,
    isFormValid,
  };
};

export default useCustomFormValidation;
